import { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TimeInput } from "@/components/ui/time-input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FileSpreadsheet } from "lucide-react";

export default function MyHoursPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [timeIn, setTimeIn] = useState("09:00");
  const [timeOut, setTimeOut] = useState("17:00");
  const [breakMin, setBreakMin] = useState(30);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/time-entries"); setEntries(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!user?.linked_employee_id) return toast.error("Your account is not linked to an employee record.");
    setSaving(true);
    try {
      await api.post("/time-entries", {
        employee_id: user.linked_employee_id,
        date, time_in: timeIn, time_out: timeOut, break_minutes: Number(breakMin), notes: notes || undefined,
      });
      toast.success("Entry submitted");
      setNotes("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!user?.linked_employee_id) {
    return (
      <div className="space-y-6" data-testid="my-hours-page">
        <h1 className="font-display text-4xl font-bold tracking-tight">My hours</h1>
        <Alert className="border-amber-200 bg-amber-50 text-amber-800">
          <AlertDescription>Your user account is not linked to an employee record. Please ask your administrator to link it.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-hours-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="tiny-label">Self-service</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">My hours</h1>
          <p className="text-muted-foreground mt-1">Submit your hours for the open period. HR will review and approve.</p>
        </div>
        {user?.linked_employee_id && (
          <Button variant="outline" onClick={async () => {
            const end = new Date().toISOString().slice(0, 10);
            const startDate = new Date(); startDate.setDate(startDate.getDate() - 90);
            const start = startDate.toISOString().slice(0, 10);
            try {
              const res = await fetch(`${API}/exports/my-hours/csv?start=${start}&end=${end}`, { credentials: "include" });
              if (!res.ok) throw new Error("Export failed");
              const blob = await res.blob();
              const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `my-hours-${start}-to-${end}.csv`; document.body.appendChild(a); a.click(); a.remove();
              toast.success("Export downloaded");
            } catch (e) { toast.error(e.message); }
          }} data-testid="export-my-hours">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Export last 90 days
          </Button>
        )}
      </div>

      <Card className="border-border shadow-none">
        <CardContent className="p-6">
          <form className="grid lg:grid-cols-12 gap-4" onSubmit={submit}>
            <div className="lg:col-span-3"><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div className="lg:col-span-2"><Label>Time in</Label><TimeInput value={timeIn} onChange={(e) => setTimeIn(e.target.value)} /></div>
            <div className="lg:col-span-2"><Label>Time out</Label><TimeInput value={timeOut} onChange={(e) => setTimeOut(e.target.value)} /></div>
            <div className="lg:col-span-2"><Label>Break (min)</Label><Input type="number" value={breakMin} onChange={(e) => setBreakMin(e.target.value)} /></div>
            <div className="lg:col-span-3 flex items-end"><Button type="submit" className="w-full h-10" disabled={saving} data-testid="myhours-submit">{saving ? "Submitting…" : "Submit"}</Button></div>
            <div className="lg:col-span-12"><Label>Notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-border shadow-none">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left tiny-label">
                <th className="px-4 py-3">Date</th><th className="px-4 py-3">In</th><th className="px-4 py-3">Out</th>
                <th className="px-4 py-3 text-right">Break</th><th className="px-4 py-3 text-right">Hours</th><th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-4 py-3 font-mono">{r.date}</td>
                  <td className="px-4 py-3">{r.time_in}</td>
                  <td className="px-4 py-3">{r.time_out}</td>
                  <td className="px-4 py-3 text-right">{r.break_minutes}m</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{r.total_hours.toFixed(2)}</td>
                  <td className="px-4 py-3 capitalize">{r.status}</td>
                </tr>
              ))}
              {entries.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No entries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
